import { get, send } from "../api.js";
import { DENSE_ROW_PAGE_SIZE, INBOX_THREAD_MESSAGE_PAGE_SIZE } from "../paginationPageSizes.js";

/**
 * The Inbox's half of the API contract, in one place.
 *
 * Staff and families call exactly the same endpoints — a conversation between
 * them is one object, and two client-side APIs over it is how the two halves
 * of a screen start disagreeing about what a conversation is.
 */

export interface InboxLatestMessage {
  subject: string;
  preview: string;
  senderName: string;
  publishedAt: string;
  importance: "normal" | "important";
}

export interface InboxConversation {
  id: string;
  kind: "student_family" | "staff_direct" | "broadcast" | "system";
  replyPolicy: "participants" | "private_reply" | "none";
  studentId: string | null;
  title: string;
  participants: string;
  lastMessageAt: string;
  messageCount: number;
  unreadCount: number;
  hasUnreadImportant: boolean;
  latest: InboxLatestMessage | null;
}

export interface InboxConversationPage {
  items: InboxConversation[];
  page: number;
  pages: number;
  total: number;
}

export interface InboxMessage {
  id: string;
  threadId: string;
  subject: string;
  body: string;
  kind: string;
  importance: "normal" | "important";
  publishedAt: string;
  readAt: string | null;
  senderName: string;
  mine: boolean;
  /** Present only when the Hub resolved a currently authorized lesson context. */
  contextHref?: string;
}

export interface InboxThread {
  id: string;
  kind: string;
  replyPolicy: string;
  studentId: string | null;
  studentName: string | null;
  title: string;
  participants: string;
  authority: "staff" | "family";
  canReply: boolean;
  replyUnavailableReason?: "announcement" | "not_participant" | "no_recipients" | null;
  /** The student record to deep-link to, or null when there is nothing to open. */
  openStudentId: string | null;
}

export interface InboxThreadView {
  thread: InboxThread;
  messages: {
    items: InboxMessage[];
    nextCursor?: string | null;
    page: number;
    pages: number;
    total: number;
    unreadCount: number;
  };
}

export interface InboxUnread {
  messages: number;
  conversations: number;
}

export interface StaffIdentity {
  accountId: string;
  name: string;
  role: string;
}

export type InboxFilter = "all" | "unread";

export type AnnouncementAudience = "all_families" | "all_staff";

export interface SentAnnouncement {
  threadId: string;
  messageId: string;
  subject: string;
  audience: AnnouncementAudience;
  importance: "normal" | "important";
  publishedAt: string;
  recipientCount: number;
  readCount: number;
  email: { requested: number; sent: number; failed: number } | null;
}

export interface SentAnnouncementPage {
  items: SentAnnouncement[];
  page: number;
  pages: number;
  total: number;
}

/** How an audience is named on screen, in one place so it reads the same everywhere. */
export const AUDIENCE_LABELS: Record<AnnouncementAudience, string> = {
  all_families: "Families",
  all_staff: "Staff",
};

export const inboxApi = {
  unread: () => get<InboxUnread>("/inbox/unread"),

  conversations: (filter: InboxFilter, page: number) =>
    get<InboxConversationPage>("/inbox/threads", {
      filter,
      page: String(page),
      pageSize: String(DENSE_ROW_PAGE_SIZE),
    }),

  thread: (threadId: string, page = 1) =>
    get<InboxThreadView>(`/inbox/threads/${encodeURIComponent(threadId)}`, {
      page: String(page),
      pageSize: String(INBOX_THREAD_MESSAGE_PAGE_SIZE),
    }),
  earlier: (threadId: string, before: string) => get<InboxThreadView>(`/inbox/threads/${encodeURIComponent(threadId)}`, {
    before, pageSize: String(INBOX_THREAD_MESSAGE_PAGE_SIZE),
  }),

  /** Opens the standing conversation, creating it the first time. */
  openStudentConversation: (studentId: string) =>
    send<{ threadId: string }>("POST", "/inbox/threads", {
      kind: "student_family",
      studentId,
    }),

  openStaffConversation: (accountId: string) =>
    send<{ threadId: string }>("POST", "/inbox/threads", {
      kind: "staff_direct",
      accountId,
    }),

  staff: () => get<{ staff: StaffIdentity[] }>("/inbox/staff"),

  reply: (
    threadId: string,
    requestId: string,
    message: { body: string; subject?: string; importance?: "normal" | "important"; lessonId?: string },
  ) =>
    send<{ message: InboxMessage; recipientCount: number }>(
      "POST",
      "/inbox/threads/" + encodeURIComponent(threadId) + "/messages",
      message,
      { "Idempotency-Key": requestId },
    ),

  /**
   * `requestId` identifies the publication attempt, not the announcement.
   *
   * It has to survive a retry of the same click, so the composer generates one
   * when the form is submitted and keeps it until the server accepts. Without
   * it a lost response sends another Inbox copy, another bell and another
   * email; the server refuses the request rather than guessing.
   */
  publishAnnouncement: (
    requestId: string,
    announcement: {
      audience: AnnouncementAudience;
      subject: string;
      body: string;
      importance: "normal" | "important";
      alsoEmail: boolean;
    },
  ) =>
    send<{ threadId: string; messageId: string; recipientCount: number }>(
      "POST",
      "/inbox/announcements",
      announcement,
      { "Idempotency-Key": requestId },
    ),

  sentAnnouncements: (page: number) =>
    get<SentAnnouncementPage>("/inbox/announcements", {
      page: String(page),
      pageSize: String(DENSE_ROW_PAGE_SIZE),
    }),

  markThreadRead: (threadId: string) =>
    send<{ marked: number }>(
      "PATCH",
      `/inbox/threads/${encodeURIComponent(threadId)}/read`,
    ),
};
